import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Divider,
    IconButton,
    Autocomplete,
    Avatar,
    Chip,
    Grid,
    Dialog,
    DialogContent,
    DialogTitle,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
} from '@mui/material';
import {
    ImageSearch,
    Close,
    FileDownload,
    AddPhotoAlternate,
    CameraAlt,
    Style,
    Tune,
    CameraRoll,
    UploadFile,
    Delete,
    Male,
    Female,
    AutoAwesome,
    CloudUpload,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { WarehouseAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/common/PageHeader';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';

// ─── Constants ────────────────────────────────────────────────────────────────

const LIGHTING_OPTIONS = [
    { value: 'Studio lighting (balanced, neutral key light)', label: 'Studio — Balanced / Neutral' },
    { value: 'Key light + Edge/Rim light creating product separation from background', label: 'Key + Edge Light' },
    { value: 'Low-key lighting with controlled fill light, deep shadows', label: 'Low Key + Fill Light' },
    { value: 'Cut lighting — single directional source highlighting only the product silhouette', label: 'Cut Light — Product Highlight Only' },
    { value: 'Hard light creating sharp product shadows on surface', label: 'Hard Light (Product Shadows)' },
    { value: 'Natural sunlight (warm, soft shadows)', label: 'Natural Sunlight' },
    { value: 'Soft diffused light, subtle highlights, no harsh shadows', label: 'Soft Diffused Light' },
    { value: 'Dramatic high-contrast moody lighting', label: 'Dramatic / Moody' },
    { value: 'Golden hour warm glow with long directional shadows', label: 'Golden Hour' },
];

const ANGLE_OPTIONS = [
    { value: 'Front view (eye-level)', label: 'Front View — Eye Level' },
    { value: 'Top view (flat lay directly above product)', label: 'Top View — Flat Lay' },
    { value: '45-degree isometric angle', label: '45° Isometric' },
    { value: 'Low angle hero perspective looking up at product', label: 'Low Angle — Hero Shot' },
    { value: 'Side profile view', label: 'Side Profile' },
    { value: 'Close-up macro detail shot', label: 'Close-Up Macro Detail' },
];

const IMAGE_TYPE_OPTIONS = [
    { value: 'E-commerce white background product shot', label: '🛒 E-Commerce (White BG)' },
    { value: 'Lifestyle tabletop product flat lay with styled props and surface texture', label: '🪴 Lifestyle — Tabletop' },
    { value: 'Lifestyle in-context shot with a model\'s hands or partial body interacting with the product naturally', label: '🤝 Lifestyle — Hands / Body (Model)' },
    { value: 'Premium gift box and packaging layout', label: '🎁 Gift Box / Packaging' },
    { value: 'Hero banner with vibrant abstract or gradient background', label: '🎨 Hero Banner (Abstract BG)' },
    { value: 'Worn by a model in a natural lifestyle setting', label: '👗 Full Model Lifestyle Shot' },
];

const SHOOT_SETUP_OPTIONS = [
    {
        value: 'masculineSetup',
        label: "Men's / Dark & Bold",
        gender: 'male',
        description: 'Dark textured backgrounds: black rock, concrete, slate. Hard dramatic contrast. Deep shadows. Bold, premium masculine feel.',
        promptText: 'Dark masculine setup: rough black rock or dark slate surface, dramatic hard lighting, deep contrasting shadows, premium bold masculine aesthetic',
    },
    {
        value: 'feminineSetup',
        label: "Women's / Soft & Elegant",
        gender: 'female',
        description: 'Light pastel or white marble backgrounds. Soft dreamy light. Premium feminine feel.',
        promptText: 'Soft feminine setup: light pastel or white marble surface, soft dreamy diffused lighting, clean airy premium feminine aesthetic. No props, no flowers, no extra decorations.',
    },
    {
        value: 'neutralSetup',
        label: 'Neutral / Versatile',
        gender: 'neutral',
        description: 'Clean, minimal neutral tone. Works for all genders. Timeless premium look.',
        promptText: 'Clean neutral minimal setup: neutral tones, balanced professional lighting, versatile timeless premium product photography',
    },
];

const LENS_OPTIONS = [
    { value: 'Macro lens 90mm — extreme close-up detail, very shallow depth of field', label: '🔬 Macro 90mm — Extreme Detail' },
    { value: 'Macro lens 100mm — sharp product detail with creamy background blur', label: '🔬 Macro 100mm — Sharp Detail + Blur' },
    { value: 'Macro lens 105mm — telephoto macro, compressed look, soft bokeh', label: '🔬 Macro 105mm — Telephoto Macro' },
    { value: '85mm portrait lens — flattering compression, soft background bokeh', label: '📸 85mm Portrait — Soft Bokeh' },
    { value: '50mm standard lens — natural perspective, balanced depth', label: '📷 50mm Standard — Natural Look' },
    { value: '35mm wide lens — environmental context, product in setting', label: '🌅 35mm Wide — Environmental' },
    { value: '24mm ultra-wide lens — dramatic perspective, product dominates frame', label: '🎬 24mm Ultra-Wide — Dramatic' },
];

const DIMENSION_OPTIONS = [
    { value: '1:1', label: '1:1 Square' },
    { value: '16:9', label: '16:9 Landscape' },
    { value: 'reel', label: 'Reel / Story (9:16)' },
    { value: 'banner', label: 'Banner (4:1)' },
    { value: 'other', label: 'Other' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const ImageGeneration: React.FC = () => {
    const { user } = useAuth();
    // Product selection
    const [skus, setSkus] = useState<any[]>([]);
    const [selectedSkus, setSelectedSkus] = useState<any[]>([]);
    const [skuInput, setSkuInput] = useState('');
    const [loadingSkus, setLoadingSkus] = useState(false);
    
    // Replacement flow SKU search
    const [replacementSkuOptions, setReplacementSkuOptions] = useState<any[]>([]);
    const [replacementSkuInput, setReplacementSkuInput] = useState('');
    const [loadingReplacementSkus, setLoadingReplacementSkus] = useState(false);

    // Model
    const [model, setModel] = useState('gemini-3.1-flash');
    const [replacementModel, setReplacementModel] = useState('gemini-3.1-flash');

    // Photography settings
    const [lighting, setLighting] = useState(LIGHTING_OPTIONS[0].value);
    const [angle, setAngle] = useState(ANGLE_OPTIONS[0].value);
    const [imageType, setImageType] = useState(IMAGE_TYPE_OPTIONS[0].value);
    const [shootSetup, setShootSetup] = useState<string>('neutralSetup');
    const [lens, setLens] = useState(LENS_OPTIONS[1].value);
    const [dimensions, setDimensions] = useState('1:1');

    // Custom prompt & reference image
    const [customPrompt, setCustomPrompt] = useState('');
    const [referenceImages, setReferenceImages] = useState<Array<{ base64: string; name: string }>>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Output
    const [generating, setGenerating] = useState(false);
    const [addingLastAsReference, setAddingLastAsReference] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const location = useLocation();

    // History / logs
    const [logs, setLogs] = useState<any[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPage, setLogsPage] = useState(0);
    const [deletingLogId, setDeletingLogId] = useState<string | null>(null);
    const [historyPreviewUrl, setHistoryPreviewUrl] = useState<string | null>(null);
    const [replacementBaseImage, setReplacementBaseImage] = useState<string | null>(null);
    const [selectedReplacementSkus, setSelectedReplacementSkus] = useState<any[]>([]);
    const [replacementInstructions, setReplacementInstructions] = useState('');
    const [replacing, setReplacing] = useState(false);
    const replacementFileInputRef = useRef<HTMLInputElement>(null);
    const productFileInputRef = useRef<HTMLInputElement>(null);

    const canDeleteHistory = (user?.email || '').toLowerCase() === 'rishabh.kumar@salty.co.in';

    // ── Load history ────────────────────────────────────────────────────────
    const loadLogs = async () => {
        setLogsLoading(true);
        try {
            const result = await WarehouseAPI.getImageGenerationLogs(30, 1);
            setLogs(result.logs || []);
        } catch (err) {
            console.error('Failed to load image generation logs', err);
        } finally {
            setLogsLoading(false);
        }
    };

    useEffect(() => { loadLogs(); }, []);

    // Reset the history pager whenever the logs list reloads (generate/refresh/delete).
    useEffect(() => { setLogsPage(0); }, [logs]);

    // ── SKU search ──────────────────────────────────────────────────────────
    // Prefill SKUs from navigation state (e.g. from Bundle Combination page)
    useEffect(() => {
        const state = location.state as any;
        if (state?.prefillSkus && Array.isArray(state.prefillSkus) && state.prefillSkus.length > 0) {
            const fetchPrefilled = async () => {
                try {
                    setLoadingSkus(true);
                    // Fetch all prefilled SKUs
                    const promises = state.prefillSkus.map((sku: string) => WarehouseAPI.searchSkusFromRedshift(sku));
                    const results = await Promise.all(promises);
                    // Flatten and filter perfect matches
                    const foundItems = results.flat().filter(item => state.prefillSkus.includes(item.sku));

                    // Deduplicate
                    const uniqueItems = Array.from(new Map(foundItems.map(item => [item.sku, item])).values());

                    if (uniqueItems.length > 0) {
                        setSelectedSkus(uniqueItems);
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch SKUs from state', err);
                } finally {
                    setLoadingSkus(false);
                }
            };
            fetchPrefilled();
        }
    }, [location.state]);
    useEffect(() => {
        const fetchSkus = async () => {
            if (!skuInput.trim()) {
                setSkus([]);
                return;
            }
            setLoadingSkus(true);
            try {
                const items = await WarehouseAPI.searchSkusFromRedshift(skuInput);
                setSkus(items);
            } catch (err) {
                console.error('Failed to fetch SKUs', err);
            } finally {
                setLoadingSkus(false);
            }
        };
        const t = setTimeout(fetchSkus, 500);
        return () => clearTimeout(t);
    }, [skuInput]);

    useEffect(() => {
        const fetchReplacementSkus = async () => {
            if (!replacementSkuInput.trim()) {
                setReplacementSkuOptions([]);
                return;
            }
            setLoadingReplacementSkus(true);
            try {
                const items = await WarehouseAPI.searchSkusFromRedshift(replacementSkuInput);
                setReplacementSkuOptions(items);
            } catch (err) {
                console.error('Failed to fetch replacement SKUs', err);
            } finally {
                setLoadingReplacementSkus(false);
            }
        };
        const t = setTimeout(fetchReplacementSkus, 500);
        return () => clearTimeout(t);
    }, [replacementSkuInput]);

    // ── Reference image upload ──────────────────────────────────────────────
    const handleReferenceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        try {
            const converted = await Promise.all(
                files.map(
                    (file) =>
                        new Promise<{ base64: string; name: string }>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (ev) => resolve({ base64: ev.target?.result as string, name: file.name });
                            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
                            reader.readAsDataURL(file);
                        })
                )
            );
            setReferenceImages((prev) => [...prev, ...converted]);
        } catch (err: any) {
            setError(err?.message || 'Failed to upload reference image(s).');
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // ── Generate ────────────────────────────────────────────────────────────
    const handleGenerate = async () => {
        if (selectedSkus.length === 0 && referenceImages.length === 0) {
            setError('Please select at least one SKU or upload at least one reference image.');
            return;
        }
        setError(null);
        setGenerating(true);
        setPreviewUrl(null);

        const selectedSetup = SHOOT_SETUP_OPTIONS.find(s => s.value === shootSetup);

        try {
            const payload = {
                components: selectedSkus.map(s => ({
                    sku: s.sku,
                    name: s.name,
                    category: s.category || '',
                    imageUrl: s.imageUrl || null,
                })),
                model,
                settings: {
                    lighting,
                    angle,
                    imageType,
                    shootSetup: selectedSetup?.promptText || '',
                    lens,
                    customPrompt: customPrompt.trim(),
                    dimensions,
                },
                referenceImagesBase64: referenceImages.map((item) => item.base64),
            };
            const { imageUrl, previewUrl: signedPreviewUrl } = await WarehouseAPI.generateCustomImage(payload);
            setPreviewUrl(signedPreviewUrl || imageUrl);
            setPreviewOpen(true);
            loadLogs();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Failed to generate image.');
        } finally {
            setGenerating(false);
        }
    };

    const downloadImage = async (url?: string) => {
        const target = url || previewUrl;
        if (!target) return;
        try {
            const response = await fetch(target);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `Salty_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch {
            // Fallback for same-origin or data URLs
            const link = document.createElement('a');
            link.href = target;
            link.download = `Salty_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const convertImageUrlToDataUrl = async (url: string): Promise<string> => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch generated image for reiteration.');
        }
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to process generated image.'));
            reader.readAsDataURL(blob);
        });
    };

    const addLastResultAsReference = async () => {
        if (!previewUrl) return;
        setAddingLastAsReference(true);
        setError(null);
        try {
            const dataUrl = await convertImageUrlToDataUrl(previewUrl);
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            setReferenceImages((prev) => [
                ...prev,
                { base64: dataUrl, name: `last-generated-${timestamp}.png` },
            ]);
            setPreviewOpen(false);
        } catch (err: any) {
            setError(err?.message || 'Failed to add last generated image as reference.');
        } finally {
            setAddingLastAsReference(false);
        }
    };

    const selectedSetupObj = SHOOT_SETUP_OPTIONS.find(s => s.value === shootSetup);

    const handleDeleteLog = async (logId: string) => {
        if (!canDeleteHistory || !logId) return;
        setDeletingLogId(logId);
        setError(null);
        try {
            await WarehouseAPI.deleteImageGenerationLog(logId);
            setLogs((prev) => prev.filter((log) => log._id !== logId));
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || 'Failed to delete image history.');
        } finally {
            setDeletingLogId(null);
        }
    };

    const handleReplacementBaseUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setReplacementBaseImage(ev.target?.result as string);
        reader.readAsDataURL(file);
    };

    const handleProductImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        try {
            const converted = await Promise.all(
                files.map(
                    (file) =>
                        new Promise<any>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (ev) => resolve({
                                sku: `UPLOADED-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
                                name: 'User Upload',
                                imageUrl: ev.target?.result as string,
                                category: 'Jewelry',
                            });
                            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
                            reader.readAsDataURL(file);
                        })
                )
            );
            setSelectedReplacementSkus((prev) => [...prev, ...converted]);
        } catch (err: any) {
            setError(err?.message || 'Failed to upload product image(s).');
        } finally {
            if (productFileInputRef.current) productFileInputRef.current.value = '';
        }
    };

    const handleExecuteReplacement = async () => {
        if (!replacementBaseImage || selectedReplacementSkus.length === 0) {
            setError('Please upload a source image and select at least one jewelry product for replacement.');
            return;
        }
        setError(null);
        setReplacing(true);
        setPreviewUrl(null);

        try {
            const payload = {
                mode: 'replacement',
                replacementBaseImage,
                components: selectedReplacementSkus.map(s => ({
                    sku: s.sku,
                    name: s.name,
                    category: s.category || '',
                    imageUrl: s.imageUrl || null,
                })),
                model: replacementModel,
                settings: {
                    dimensions,
                    customPrompt: replacementInstructions.trim(),
                },
            };
            const { imageUrl, previewUrl: signedPreviewUrl } = await WarehouseAPI.generateCustomImage(payload);
            setPreviewUrl(signedPreviewUrl || imageUrl);
            setPreviewOpen(true);
            loadLogs();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Failed to execute replacement.');
        } finally {
            setReplacing(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2, pb: 3 }}>
            {/* Header */}
            <PageHeader
                icon={<CameraAlt />}
                title="AI Image Generation"
                subtitle="Professional product photography powered by advanced AI — configure every detail of your shoot."
            />

            {error && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2 }}>
                    <Typography color="#b91c1c" fontWeight={600}>⚠️ {error}</Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {/* ── LEFT COLUMN ── */}
                <Grid size={{ xs: 12, lg: 8 }}>

                    {/* Step 1: Products */}
                    <Card sx={{ mb: 3, overflow: 'visible' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AddPhotoAlternate sx={{ color: '#DC5829' }} /> Step 1: Select Products
                            </Typography>

                            <Autocomplete
                                multiple
                                id="sku-picker"
                                options={skus}
                                loading={loadingSkus}
                                getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                                value={selectedSkus}
                                onChange={(_, v) => setSelectedSkus(v)}
                                inputValue={skuInput}
                                onInputChange={(_, v) => setSkuInput(v)}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search and add SKUs"
                                        placeholder="Type SKU or Name..."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingSkus ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar src={option.imageUrl} variant="rounded" sx={{ width: 40, height: 40 }} />
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>{option.sku}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            avatar={<Avatar src={option.imageUrl} />}
                                            label={option.sku}
                                            {...getTagProps({ index })}
                                            sx={{ bgcolor: '#FAFAF7', fontWeight: 600, border: '1px solid #ECECE4' }}
                                        />
                                    ))
                                }
                            />

                            {selectedSkus.length > 0 && (
                                <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, p: 2, bgcolor: '#FAFAF7', borderRadius: 2, border: '1px dashed #DAD6CC' }}>
                                    {selectedSkus.map(sku => (
                                        <Box key={sku.sku} sx={{ width: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={sku.imageUrl} variant="rounded" sx={{ width: 64, height: 64, bgcolor: '#fff', border: '1px solid #ECECE4' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', textAlign: 'center', wordBreak: 'break-all' }}>
                                                {sku.sku}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Step 2: Photography Settings */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CameraAlt sx={{ color: '#DC5829' }} /> Step 2: Photography Settings
                            </Typography>

                            <Grid container spacing={3}>
                                {/* AI Model */}
                                <Grid size={{ xs: 12 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>AI Model</InputLabel>
                                        <Select value={model} label="AI Model" onChange={(e) => setModel(e.target.value)}>
                                            <MenuItem value="gemini-3.1-flash">Gemini 3.1 Flash</MenuItem>
                                            <MenuItem value="gpt-image-1">GPT Image Gen</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Shoot Setup */}
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#4B5563', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AutoAwesome sx={{ fontSize: 16, color: '#DC5829' }} /> Shoot Setup & Mood
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {SHOOT_SETUP_OPTIONS.map(opt => (
                                            <Grid key={opt.value} size={{ xs: 12, sm: 4 }}>
                                                <Paper
                                                    onClick={() => setShootSetup(opt.value)}
                                                    elevation={0}
                                                    sx={{
                                                        p: 2, cursor: 'pointer', borderRadius: 2, transition: 'all 0.2s',
                                                        border: shootSetup === opt.value ? '2px solid #DC5829' : '2px solid #e5e7eb',
                                                        bgcolor: shootSetup === opt.value ? 'rgba(220, 88, 41, 0.06)' : '#fff',
                                                        '&:hover': { borderColor: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.06)' },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        {opt.gender === 'male' ? <Male sx={{ color: '#B8451E', fontSize: 18 }} /> :
                                                            opt.gender === 'female' ? <Female sx={{ color: '#EC4899', fontSize: 18 }} /> :
                                                                <AutoAwesome sx={{ color: '#DC5829', fontSize: 18 }} />}
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: shootSetup === opt.value ? '#B8451E' : '#374151' }}>
                                                            {opt.label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: '#6B625B', lineHeight: 1.4 }}>
                                                        {opt.description}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ my: 0.5 }} />
                                </Grid>

                                {/* Lighting */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>💡 Lighting Conditions</InputLabel>
                                        <Select value={lighting} label="💡 Lighting Conditions" onChange={(e) => setLighting(e.target.value)}>
                                            {LIGHTING_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Camera Angle */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>📐 Camera Angle</InputLabel>
                                        <Select value={angle} label="📐 Camera Angle" onChange={(e) => setAngle(e.target.value)}>
                                            {ANGLE_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Image Type */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>🖼️ Image Type</InputLabel>
                                        <Select value={imageType} label="🖼️ Image Type" onChange={(e) => setImageType(e.target.value)}>
                                            {IMAGE_TYPE_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Lens */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>🔭 Lens Selection</InputLabel>
                                        <Select value={lens} label="🔭 Lens Selection" onChange={(e) => setLens(e.target.value)}>
                                            {LENS_OPTIONS.map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Dimensions */}
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#4B5563', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CameraRoll sx={{ fontSize: 16, color: '#DC5829' }} /> Image Dimensions
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={dimensions}
                                        exclusive
                                        onChange={(_, v) => v && setDimensions(v)}
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                py: 1, px: 2, border: '2px solid #e5e7eb', borderRadius: '8px !important',
                                                mr: 1, mb: 1, textTransform: 'none', fontWeight: 600, color: '#6B625B',
                                                '&.Mui-selected': {
                                                    borderColor: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.06)', color: '#B8451E',
                                                    '&:hover': { bgcolor: 'rgba(220, 88, 41, 0.08)' }
                                                },
                                                '&:hover': { borderColor: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.06)' },
                                            }
                                        }}
                                    >
                                        {DIMENSION_OPTIONS.map(opt => (
                                            <ToggleButton key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Step 3: Customisation */}
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tune sx={{ color: '#DC5829' }} /> Step 3: Custom Instructions & Reference
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Custom Prompt */}
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="✍️ Additional Instructions (optional)"
                                        placeholder="e.g. Add a dry ice smoke effect around the base. The product should be placed on a black granite slab. Include warm bokeh lights in the background..."
                                        value={customPrompt}
                                        onChange={(e) => setCustomPrompt(e.target.value)}
                                        helperText={`${customPrompt.length}/1000 characters — describe any extra details, props, atmosphere, or styling`}
                                        inputProps={{ maxLength: 1000 }}
                                    />
                                </Grid>

                                {/* Reference Image */}
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#4B5563' }}>
                                        📎 Reference Image (optional)
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleReferenceUpload}
                                    />
                                    {referenceImages.length === 0 ? (
                                        <Box
                                            onClick={() => fileInputRef.current?.click()}
                                            sx={{
                                                border: '2px dashed #d1d5db', borderRadius: 2, p: 3,
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                                                cursor: 'pointer', transition: 'all 0.2s',
                                                '&:hover': { borderColor: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.06)' },
                                            }}
                                        >
                                            <UploadFile sx={{ fontSize: 36, color: '#8A8078' }} />
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                Upload reference image(s) for style / composition inspiration
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                PNG, JPG, WEBP — max 10MB each
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ color: '#166534' }}>
                                                    ✅ {referenceImages.length} reference image(s) attached
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    sx={{ color: '#166534', fontWeight: 700 }}
                                                >
                                                    Add More
                                                </Button>
                                            </Box>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                                                {referenceImages.map((item, index) => (
                                                    <Box key={`${item.name}-${index}`} sx={{ position: 'relative' }}>
                                                        <Box
                                                            component="img"
                                                            src={item.base64}
                                                            alt={item.name}
                                                            sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 1, border: '1px solid #d1fae5' }}
                                                        />
                                                        <Tooltip title="Remove reference image">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setReferenceImages((prev) => prev.filter((_, i) => i !== index))}
                                                                sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.9)', color: '#EF4444' }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {item.name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ── RIGHT COLUMN ── */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Card sx={{ position: 'sticky', top: 24 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Style sx={{ color: '#DC5829' }} /> Step 4: Generate
                            </Typography>

                            {/* Shot Summary */}
                            <Box sx={{ p: 2.5, bgcolor: '#FAFAF7', borderRadius: 2, mb: 3, border: '1px solid #ECECE4' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155', mb: 1.5 }}>
                                    📋 Shot Summary
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Products:</b> {selectedSkus.length > 0 ? selectedSkus.map(s => s.sku).join(', ') : 'None selected'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Model:</b> {model}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Setup:</b> {selectedSetupObj?.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Lighting:</b> {LIGHTING_OPTIONS.find(l => l.value === lighting)?.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Type:</b> {IMAGE_TYPE_OPTIONS.find(t => t.value === imageType)?.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Lens:</b> {LENS_OPTIONS.find(l => l.value === lens)?.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                        <b>Dimensions:</b> {DIMENSION_OPTIONS.find(d => d.value === dimensions)?.label}
                                    </Typography>
                                    {customPrompt && (
                                        <Typography variant="body2" sx={{ color: '#6B625B' }}>
                                            <b>Custom:</b> {customPrompt.slice(0, 60)}{customPrompt.length > 60 ? '…' : ''}
                                        </Typography>
                                    )}
                                    {referenceImages.length > 0 && (
                                        <Typography variant="body2" sx={{ color: '#16a34a' }}>
                                            ✅ References: {referenceImages.length}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleGenerate}
                                disabled={generating || (selectedSkus.length === 0 && referenceImages.length === 0)}
                                startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                                sx={{
                                    py: 2, mb: 2, fontWeight: 700, fontSize: '1rem',
                                    background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                                    boxShadow: '0 4px 20px rgba(220,88,41,0.4)',
                                    '&:hover': { boxShadow: '0 6px 28px rgba(220,88,41,0.5)' },
                                }}
                            >
                                {generating ? 'Generating…' : '✨ Generate Image'}
                            </Button>

                            {previewUrl && (
                                <>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => setPreviewOpen(true)}
                                        startIcon={<ImageSearch />}
                                        sx={{ mb: 2, fontWeight: 700, borderColor: '#DC5829', color: '#DC5829' }}
                                    >
                                        View Last Result
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={addLastResultAsReference}
                                        disabled={addingLastAsReference}
                                        startIcon={addingLastAsReference ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                                        sx={{ mb: 2, fontWeight: 700, borderColor: '#16a34a', color: '#16a34a' }}
                                    >
                                        {addingLastAsReference ? 'Adding as Reference…' : 'Use Last Result as Reference'}
                                    </Button>
                                    <Box
                                        component="img"
                                        src={previewUrl}
                                        alt="Last generated"
                                        onClick={() => setPreviewOpen(true)}
                                        sx={{
                                            width: '100%', borderRadius: 2, border: '1px solid #ECECE4',
                                            cursor: 'pointer', transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.01)' },
                                        }}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Preview Modal */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CameraAlt sx={{ color: '#DC5829' }} />
                        <Typography fontWeight={700}>AI Generated Result</Typography>
                        <Chip label={model} size="small" sx={{ bgcolor: 'rgba(220, 88, 41, 0.06)', color: '#B8451E', fontWeight: 700, fontSize: '0.65rem', ml: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {previewUrl && (
                            <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() => downloadImage()}
                                sx={{ borderColor: '#DC5829', color: '#DC5829' }}>
                                Download
                            </Button>
                        )}
                        {previewUrl && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={addingLastAsReference ? <CircularProgress size={14} color="inherit" /> : <AutoAwesome />}
                                onClick={addLastResultAsReference}
                                disabled={addingLastAsReference}
                                sx={{ borderColor: '#16a34a', color: '#16a34a' }}
                            >
                                {addingLastAsReference ? 'Adding…' : 'Use as Reference'}
                            </Button>
                        )}
                        <IconButton onClick={() => setPreviewOpen(false)} size="small"><Close /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, bgcolor: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 500 }}>
                    {previewUrl ? (
                        <Box component="img" src={previewUrl} alt="AI Generated" sx={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }} />
                    ) : (
                        <Typography color="text.secondary">No image generated yet.</Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* History Preview Modal */}
            <Dialog open={!!historyPreviewUrl} onClose={() => setHistoryPreviewUrl(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight={700}>Generated Image</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {historyPreviewUrl && (
                            <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() => downloadImage(historyPreviewUrl)}
                                sx={{ borderColor: '#DC5829', color: '#DC5829' }}>
                                Download
                            </Button>
                        )}
                        <IconButton onClick={() => setHistoryPreviewUrl(null)} size="small"><Close /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, bgcolor: '#0f0f0f', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 500 }}>
                    {historyPreviewUrl && (
                        <Box component="img" src={historyPreviewUrl} alt="History" sx={{ width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }} />
                    )}
                </DialogContent>
            </Dialog>

            {/* Jewelry Replacement Mode (Product Swap) */}
            <Card sx={{ mt: 5, border: '2px solid #DC5829', bgcolor: '#fdfaff' }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box>
                            <Typography variant="h5" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <AutoAwesome sx={{ color: '#DC5829' }} /> AI Jewelry Replacement (Product Swap)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Upload a model or lifestyle image and swap existing jewelry with your provided catalog products.
                            </Typography>
                        </Box>

                    </Box>

                    <Grid container spacing={4} alignItems="center">
                        {/* 1. Base Image */}
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#4B5563' }}>
                                    📸 1. Source Image
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#DC5829', fontWeight: 600 }}>(To be modified)</Typography>
                            </Box>
                            
                            <input
                                ref={replacementFileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleReplacementBaseUpload}
                            />
                            <Box
                                onClick={() => replacementFileInputRef.current?.click()}
                                sx={{
                                    height: 320, border: '2px dashed #d1d5db', borderRadius: 4,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', overflow: 'hidden', position: 'relative',
                                    bgcolor: '#f9fafb', transition: 'all 0.2s',
                                    '&:hover': { borderColor: '#DC5829', bgcolor: 'rgba(220, 88, 41, 0.06)', boxShadow: '0 4px 12px rgba(220,88,41,0.1)' },
                                }}
                            >
                                {replacementBaseImage ? (
                                    <Box 
                                        component="img" 
                                        src={replacementBaseImage} 
                                        sx={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover',
                                            transition: 'transform 0.3s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }} 
                                    />
                                ) : (
                                    <>
                                        <AddPhotoAlternate sx={{ fontSize: 48, color: '#8A8078', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>Click to upload source image</Typography>
                                        <Typography variant="caption" color="text.disabled">Supports JPG, PNG, WEBP</Typography>
                                    </>
                                )}
                                {replacementBaseImage && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); setReplacementBaseImage(null); }}
                                        sx={{ 
                                            position: 'absolute', top: 12, right: 12, 
                                            bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            '&:hover': { bgcolor: '#fff', color: '#EF4444' } 
                                        }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Grid>

                        {/* Divider */}
                        <Grid size={{ xs: 12, md: 1 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', minHeight: 200 }}>
                            <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 2, borderStyle: 'dashed', borderColor: '#e5e7eb' }}>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        px: 1, py: 0.5, bgcolor: '#DC5829', color: '#fff', 
                                        borderRadius: 1, fontWeight: 800, textTransform: 'uppercase',
                                        fontSize: '0.6rem', letterSpacing: '0.05em',
                                        transform: 'rotate(-90deg)', whiteSpace: 'nowrap'
                                    }}
                                >
                                    Replace With
                                </Typography>
                            </Divider>
                        </Grid>

                        {/* 2. Replacement SKUs */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#4B5563' }}>
                                    💍 2. Products to Swap In
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel id="replacement-model-label">AI Model</InputLabel>
                                        <Select
                                            labelId="replacement-model-label"
                                            value={replacementModel}
                                            label="AI Model"
                                            onChange={(e) => setReplacementModel(e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="gemini-3.1-flash">Gemini 3.1 Flash</MenuItem>
                                            <MenuItem value="gpt-image-1">GPT Image Gen</MenuItem>
                                        </Select>
                                    </FormControl>
                                    
                                    <input
                                        ref={productFileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleProductImageUpload}
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<CloudUpload />}
                                        onClick={() => productFileInputRef.current?.click()}
                                        sx={{ 
                                            background: '#DC5829', 
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            '&:hover': { background: '#B8451E' } 
                                        }}
                                    >
                                        Upload Image
                                    </Button>
                                </Box>
                            </Box>

                            <Autocomplete
                                multiple
                                id="replacement-sku-picker"
                                options={replacementSkuOptions}
                                loading={loadingReplacementSkus}
                                getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                                value={selectedReplacementSkus}
                                onChange={(_, v) => setSelectedReplacementSkus(v)}
                                inputValue={replacementSkuInput}
                                onInputChange={(_, v) => setReplacementSkuInput(v)}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search and add SKUs"
                                        placeholder="Type SKU or Name..."
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingReplacementSkus ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar src={option.imageUrl} variant="rounded" sx={{ width: 44, height: 44 }} />
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>{option.sku}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                                        </Box>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            avatar={<Avatar src={option.imageUrl} />}
                                            label={option.sku}
                                            {...getTagProps({ index })}
                                            sx={{ bgcolor: '#f3f4f6', fontWeight: 600, border: '1px solid #e5e7eb' }}
                                        />
                                    ))
                                }
                            />

                            <Box 
                                sx={{ 
                                    mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 3, 
                                    border: '1px solid #e5e7eb', minHeight: 120,
                                    display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'start', alignContent: 'start'
                                }}
                            >
                                {selectedReplacementSkus.map(sku => (
                                    <Box key={sku.sku} sx={{ position: 'relative', width: 70, textAlign: 'center' }}>
                                        <Avatar 
                                            src={sku.imageUrl} 
                                            variant="rounded" 
                                            sx={{ 
                                                width: 70, height: 70, mb: 0.5, 
                                                border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' 
                                            }} 
                                        />
                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', display: 'block' }}>
                                            {sku.sku.split('-')[0]}
                                        </Typography>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => setSelectedReplacementSkus(prev => prev.filter(s => s.sku !== sku.sku))}
                                            sx={{ 
                                                position: 'absolute', top: -8, right: -8, 
                                                bgcolor: '#fee2e2', color: '#EF4444', 
                                                p: 0.2, '&:hover': { bgcolor: '#fecaca' } 
                                            }}
                                        >
                                            <Close sx={{ fontSize: 12 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                                {selectedReplacementSkus.length === 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', opacity: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            Select products above to see preview here
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            
                            <Box sx={{ mt: 3 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="✍️ Additional Instructions (optional)"
                                    placeholder="describe any extra details, props, atmosphere, or styling"
                                    value={replacementInstructions}
                                    onChange={(e) => setReplacementInstructions(e.target.value)}
                                    helperText={`${replacementInstructions.length}/1000 characters — describe any extra details, props, atmosphere, or styling`}
                                    inputProps={{ maxLength: 1000 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' }
                                    }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={replacing || !replacementBaseImage || selectedReplacementSkus.length === 0}
                                onClick={handleExecuteReplacement}
                                startIcon={replacing ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                                sx={{
                                    mt: 3, py: 1.8, fontWeight: 800, borderRadius: 3,
                                    background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
                                    boxShadow: '0 8px 20px rgba(220,88,41,0.25)',
                                    textTransform: 'none', fontSize: '1rem',
                                    transition: 'all 0.3s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 25px rgba(220,88,41,0.35)' },
                                    '&.Mui-disabled': { background: '#e5e7eb' }
                                }}
                            >
                                {replacing ? 'Processing Image...' : 'Execute Jewelry Replacement'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Generation History */}
            <Box sx={{ mt: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h5" sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CameraRoll sx={{ color: '#DC5829' }} /> Generation History
                    </Typography>
                    <Button size="small" variant="outlined" onClick={loadLogs} disabled={logsLoading}
                        sx={{ borderColor: '#DC5829', color: '#DC5829' }}>
                        {logsLoading ? 'Loading…' : 'Refresh'}
                    </Button>
                </Box>

                {logsLoading && <Typography color="text.secondary">Loading history…</Typography>}
                {!logsLoading && logs.length === 0 && (
                    <Typography color="text.secondary">No images generated yet.</Typography>
                )}

                {logs.length > 0 && (
                    <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
                        {logs.slice(logsPage * DEFAULT_PAGE_SIZE, logsPage * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE).map((log: any) => {
                            const renderUrl = log.previewUrl || log.imageUrl;
                            return (
                            <Card key={log._id} sx={{ border: '1px solid #ECECE4', borderRadius: 2, overflow: 'hidden' }}>
                                <Box
                                    onClick={() => renderUrl && setHistoryPreviewUrl(renderUrl)}
                                    sx={{
                                        height: 180, bgcolor: '#0f0f0f', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', cursor: renderUrl ? 'pointer' : 'default',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {renderUrl ? (
                                        <Box component="img" src={renderUrl} alt="Generated"
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.04)' } }} />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">No image</Typography>
                                    )}
                                </Box>
                                <CardContent sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                        <Chip
                                            label={log.success ? 'Success' : 'Failed'}
                                            size="small"
                                            sx={{
                                                bgcolor: log.success ? '#dcfce7' : '#fee2e2',
                                                color: log.success ? '#15803d' : '#b91c1c',
                                                fontWeight: 700, fontSize: '0.6rem',
                                            }}
                                        />
                                        {log.mode === 'replacement' && (
                                            <Chip 
                                                label="Product Swap" 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: '#fff7ed', color: '#ea580c', 
                                                    fontWeight: 800, fontSize: '0.55rem',
                                                    border: '1px solid #ffedd5' 
                                                }} 
                                            />
                                        )}
                                        <Chip label={log.modelUsed || 'unknown'} size="small"
                                            sx={{ bgcolor: 'rgba(220, 88, 41, 0.06)', color: '#B8451E', fontWeight: 600, fontSize: '0.6rem' }} />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600, display: 'block' }}>
                                        {log.userId}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B625B', display: 'block', mb: 0.5 }}>
                                        SKUs: {(log.skus || []).join(', ') || '—'}
                                    </Typography>
                                    {(log.inputTokens > 0 || log.outputTokens > 0) && (
                                        <Typography variant="caption" sx={{ color: '#6B625B', display: 'block' }}>
                                            Tokens: {log.inputTokens.toLocaleString()} in / {log.outputTokens.toLocaleString()} out
                                        </Typography>
                                    )}
                                    {log.costUSD > 0 && (
                                        <Typography variant="caption" sx={{ color: '#059669', display: 'block', fontWeight: 700 }}>
                                            Cost: ${log.costUSD.toFixed(4)}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(log.createdAt).toLocaleString()}
                                    </Typography>
                                    {renderUrl && (
                                        <Box sx={{ mt: 0.5 }}>
                                            <Button size="small" startIcon={<FileDownload sx={{ fontSize: 14 }} />}
                                                onClick={() => downloadImage(renderUrl)}
                                                sx={{ p: 0, fontSize: '0.65rem', color: '#DC5829', minWidth: 0, textTransform: 'none' }}>
                                                Download
                                            </Button>
                                        </Box>
                                    )}
                                    {canDeleteHistory && (
                                        <Box sx={{ mt: 0.25 }}>
                                            <Button
                                                size="small"
                                                startIcon={deletingLogId === log._id ? <CircularProgress size={12} color="inherit" /> : <Delete sx={{ fontSize: 14 }} />}
                                                onClick={() => handleDeleteLog(log._id)}
                                                disabled={deletingLogId === log._id}
                                                sx={{ p: 0, fontSize: '0.65rem', color: '#EF4444', minWidth: 0, textTransform: 'none' }}
                                            >
                                                {deletingLogId === log._id ? 'Deleting...' : 'Delete'}
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        );})}
                    </Box>
                    <TablePaginationBar page={logsPage} total={logs.length} onPageChange={setLogsPage} />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ImageGeneration;
